/**
 * Donut Chart Component
 * Category breakdown with interactive segments
 */

import React from 'react'
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts'
import { chartTheme } from './chart-theme'
import { BaseChart } from './BaseChart'

export interface DonutData {
  name: string
  value: number
  color?: string
}

export interface DonutChartProps {
  data: DonutData[]
  height?: number
  loading?: boolean
  error?: Error | null
  onRetry?: () => void
  onSegmentClick?: (data: DonutData) => void
  centerText?: string
  centerValue?: string
}

const COLORS = [
  chartTheme.colors.primary,
  chartTheme.colors.positive,
  chartTheme.colors.warning,
  chartTheme.colors.negative,
  '#8B5CF6',
  '#EC4899',
  '#06B6D4'
]

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload || !payload.length) return null

  const data = payload[0]
  const percentage = data.payload.percent * 100

  return (
    <div className="bg-[rgba(35,36,41,0.95)] border border-white/10 rounded-lg p-3">
      <p className="text-sm text-foreground font-semibold mb-1">
        {data.name}
      </p>
      <span className="text-xs block" style={{ color: data.payload.fill }}>
        Valor: {data.value.toLocaleString('pt-BR')}
      </span>
      <span className="text-xs text-muted-foreground block">
        Porcentagem: {percentage.toFixed(1)}%
      </span>
    </div>
  )
}

export const DonutChart: React.FC<DonutChartProps> = ({
  data,
  height = 300,
  loading = false,
  error = null,
  onRetry,
  onSegmentClick,
  centerText,
  centerValue
}) => {
  if (!data || data.length === 0) {
    return (
      <div
        className="w-full flex items-center justify-center bg-background rounded-lg"
        style={{ height }}
      >
        <p className="text-sm text-muted-foreground">
          Dados não disponíveis
        </p>
      </div>
    )
  }

  return (
    <BaseChart
      loading={loading}
      error={error}
      onRetry={onRetry}
      width="100%"
      height={height}
      ariaLabel="Donut chart showing category breakdown"
    >
      <div className="relative">
        <ResponsiveContainer width="100%" height={height}>
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="60%"
              outerRadius="80%"
              paddingAngle={2}
              dataKey="value"
              onClick={(data) => onSegmentClick?.(data)}
              cursor={onSegmentClick ? 'pointer' : 'default'}
              animationDuration={300}
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend
              verticalAlign="bottom"
              height={36}
              wrapperStyle={{ fontSize: 12 }}
            />
          </PieChart>
        </ResponsiveContainer>

        {/* Center text overlay */}
        {(centerText || centerValue) && (
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none">
            {centerValue && (
              <h5 className="text-xl font-bold leading-none">
                {centerValue}
              </h5>
            )}
            {centerText && (
              <span className="text-xs text-muted-foreground block mt-1">
                {centerText}
              </span>
            )}
          </div>
        )}
      </div>
    </BaseChart>
  )
}
